import {HttpErrorResponse, HttpInterceptorFn, HttpResponse} from '@angular/common/http';
import {of} from 'rxjs';
import {inject} from '@angular/core';
import {environment} from '../../environments/environment';
import {ServiceProviderHub, PaginatedResponse, SingleResponse} from '@generated-backend-types';
import {MockService} from '../services/mock.service';
import TableConfig = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Meta.TableConfig;
import ServiceProviderInquiry = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Event.Inquiry.EventInquiry;
import EventmanagementEvent = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Event.Event;

export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!environment.api.mock_backend || req.url.includes('i18n')) return next(req);
  const path = req.url.replace(environment.api.url, '');

  const eventId = '01977dee-9edd-703a-9401-7de47a242043';

  switch (path) {
    case '/auth/login':
    case '/auth/logout':
    case '/auth/user':
    case '/version':
    case '/administrations/private-attachments':
    case '/eventmanagement/events/' + eventId + '/schedule/activities':
      console.log(`Delegating to Api: '${path}'`);
      return next(req);
  }

  console.log(`Mocking: '${path}'`);

  MockService.setEventId(eventId);
  const mockService = inject(MockService);

  const response = getResponse(path);

  return of(response);

  function getResponse(path: string) {
    const inquiryByIdMatch = path.match(/^\/api\/events\/[^/]+\/inquiries\/([^/]+)$/);
    if (inquiryByIdMatch) {
      return getInquiry(inquiryByIdMatch[1]);
    }
    const inquiriesListMatch = path.match(/^\/api\/events\/[^/]+\/inquiries$/);
    if (inquiriesListMatch) {
      return getInquiries();
    }
    const eventByIdMatch = path.match(/^\/api\/events\/([^/]+)$/);
    if (eventByIdMatch) {
      return getEvent(eventByIdMatch[1]);
    }

    switch (path) {
      case '/meta/table-config':
        return getTableConfig();
      case '/api/events':
        return getEvents();
      case '/settings/table-filter-favorites':
        return getTableFilterFavorites();
      default:
        console.log(`Could not mock: '${path}'`);
        return new HttpResponse({});
    }
  }

  function getEvents(): HttpResponse<PaginatedResponse<EventmanagementEvent>> {
    const events = mockService.getAllEvents();
    const page = Number.parseInt(req.params.get('page') ?? '1');
    return new HttpResponse({
      status: 200,
      body: mockService.paginate(events, page, '/'),
    });
  }

  function getEvent(id: string): HttpResponse<SingleResponse<EventmanagementEvent>> {
    const event = mockService.getEvent(id);
    if (!event) {
      throw new HttpErrorResponse({
        status: 404,
        statusText: 'Not found',
        error: `Event with Id ${id} was not found.`,
      });
    }
    return new HttpResponse({status: 200, body: {data: event}});
  }

  function getTableConfig(): HttpResponse<TableConfig[]> {
    return new HttpResponse({
      status: 200,
      body: mockService.tableConfig,
    })
  }

  function getInquiries(): HttpResponse<PaginatedResponse<ServiceProviderInquiry>> {
    const inquiries = mockService.getAllInquiries();
    const page = Number.parseInt(req.params.get('page') ?? '1');

    return new HttpResponse({
      status: 200,
      body: mockService.paginate(inquiries, page, '/'),
    });
  }

  function getInquiry(id: string): HttpResponse<SingleResponse<ServiceProviderInquiry>> {
    const inquiry = mockService.getInquiry(id);

    if (!inquiry) {
      throw new HttpErrorResponse({
        status: 404,
        statusText: 'Not found',
        error: `Inquiry with Id ${id} was not found.`
      })
    }

    return new HttpResponse({
      status: 200,
      body: {data: inquiry},
    });
  }

  function getTableFilterFavorites() {
    return new HttpResponse({
      status: 200,
      body: {
        data: [],
        meta: {
          current_page: 1,
          from: 0,
          to: 0,
          last_page: 1,
          per_page: 10,
          total: 0,
          path: '',
        }
      }
    })
  }
};
